import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  approveApplicationRequest,
  rejectApplicationRequest,
} from "../Clients/referralApplicationApi";

const MODULE_LABELS = {
  admission: "Admission",
  visa: "Visa",
  insurance: "Insurance",
  "skill-assessment": "Skill Assessment",
  "rpl-course": "RPL",
  other: "Other",
};

/** The same labels the partner filled in against, so the office reads what was meant. */
const DETAIL_LABELS = {
  courseName: "Course",
  courseId: "Course",
  universityName: "University / college",
  intake: "Intake",
  state: "State",
  visa: "Visa",
  insuranceCompany: "Insurer",
  policyType: "Insurance type",
  coverType: "Cover type",
  occupation: "Occupation",
  courseProvider: "Provider",
  certificate: "Certificate",
  otherName: "What this is for",
};

const prettyKey = (key) =>
  DETAIL_LABELS[key] ||
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (first) => first.toUpperCase());

const textOf = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return String(value).trim();
};

const Row = ({ label, value }) => (
  <div className="flex gap-3 py-1.5 border-b border-app-border last:border-0">
    <div className="w-44 shrink-0 text-sm text-app-text-muted">{label}</div>
    <div className="text-sm font-medium break-words whitespace-pre-wrap min-w-0">{value}</div>
  </div>
);

/**
 * Everything the partner submitted with a request, in one place: approving or returning it
 * blind is how known details get lost. A pending request can also be decided here, with the
 * same optional note on approve and required reason on return as the review dialogs.
 */
const ApplicationRequestDetailsDialog = ({ request, canReview, onClose, onDone }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote("");
    setBusy(false);
  }, [request?.id]);

  const details = request
    ? Object.entries(request.requestDetails || {})
        .map(([key, value]) => [prettyKey(key), textOf(value)])
        .filter(([, value]) => value)
    : [];

  const pending = request?.status === "PENDING";
  const showActions = pending && canReview;

  const finish = () => {
    onDone?.();
    onClose();
  };

  const doApprove = async () => {
    if (!request) return;
    setBusy(true);
    try {
      await approveApplicationRequest(request.id, note);
      toast.success("Approved — the application entry was created with the partner's details");
      finish();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not approve the request");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!request) return;
    if (!note.trim()) {
      toast.warning("Add a reason so the partner knows what to fix");
      return;
    }
    setBusy(true);
    try {
      await rejectApplicationRequest(request.id, note.trim());
      toast.success("Returned to the partner with your reason");
      finish();
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Could not return the request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(request)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Request details
        {request ? (
          <Typography variant="body2" color="text.secondary">
            {MODULE_LABELS[request.moduleType] || request.moduleType} for{" "}
            {request.clientName || "the client"} · requested by{" "}
            {request.partnerName || "the partner"}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {!request ? null : (
          <>
            <Row
              label="Status"
              value={
                <Chip
                  size="small"
                  color={
                    request.status === "APPROVED"
                      ? "success"
                      : request.status === "PENDING"
                      ? "warning"
                      : "default"
                  }
                  label={
                    request.status === "PENDING"
                      ? "Pending"
                      : request.status === "APPROVED"
                      ? "Approved"
                      : "Returned"
                  }
                />
              }
            />
            {request.requestNote ? (
              <Row label="What they know" value={request.requestNote} />
            ) : null}
            {details.map(([label, value]) => (
              <Row key={label} label={label} value={value} />
            ))}
            {details.length === 0 && !request.requestNote ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Just interested — no details were filled in.
              </Typography>
            ) : null}
            {request.adminNote ? <Row label="Office note" value={request.adminNote} /> : null}
            {request.reviewedBy ? (
              <Row
                label="Reviewed by"
                value={`${request.reviewedBy}${
                  request.reviewedAt ? ` · ${new Date(request.reviewedAt).toLocaleString()}` : ""
                }`}
              />
            ) : null}
          </>
        )}
      </DialogContent>
      {showActions ? (
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Note — optional on approve, required to return"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </DialogContent>
      ) : null}
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Close
        </Button>
        {showActions ? (
          <>
            <Button onClick={doReject} variant="outlined" color="error" disabled={busy}>
              {busy ? "Returning…" : "Return to partner"}
            </Button>
            <Button onClick={doApprove} variant="contained" color="success" disabled={busy}>
              {busy ? "Approving…" : "Approve & create entry"}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default ApplicationRequestDetailsDialog;
